import { useTranslation } from '@hooks/useTranslation';
import { CloseOutlined } from '@mui/icons-material';
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import WolfButton from './ui/WolfButton';

export interface CustomModalProps {
  title?: string;
  customCancelText?: string;
  customConfirmText?: string;
  customConfirmColor?: 'primary' | 'secondary' | 'error' | 'success';
  customClass?: string;
  isOpen: boolean;
  hideClose?: boolean;
  showButtons?: boolean;
  showConfirmButton?: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  children: React.ReactNode;
  maxWidth?: '400px' | '500px' |'600px' | '800px' | '1024px' | '1200px';
  maxHeight?: string;
  rounded?: boolean;
}

const CustomModal: React.FC<CustomModalProps> = ({
  title,
  customCancelText,
  customConfirmText,
  customConfirmColor,
  customClass,
  isOpen,
  hideClose,
  showButtons,
  showConfirmButton = true,
  onClose,
  onConfirm,
  children,
  maxWidth = '1200px',
  maxHeight,
  rounded = false,
}) => {
  const localeCommon = useTranslation('common');

  return (
    <Dialog
      open={isOpen}
      onClose={hideClose ? undefined : onClose}
      disableEscapeKeyDown={hideClose}
      maxWidth={false}
      PaperProps={{
        className: `${customClass} ${rounded ? 'rounded-lg' : ''}`,
        sx: {
          maxHeight: maxHeight || 'calc(100vh - 256px)',
          height: maxHeight ? maxHeight : 'auto',
          maxWidth: maxWidth,
          width: '75%',
          minHeight: '256px',
        },
      }}>
      {title ? (
        <DialogTitle>
          <Box className="flex justify-between items-center">
            <Typography variant="h3semibold" className="text-center">
              {title}
            </Typography>
            {!hideClose && (
              <IconButton onClick={onClose} color="primary">
                <CloseOutlined />
              </IconButton>
            )}
          </Box>
        </DialogTitle>
      ) : (
        !hideClose && (
          <Box className="absolute top-2 right-2">
            <IconButton onClick={onClose} color="primary">
              <CloseOutlined />
            </IconButton>
          </Box>
        )
      )}
      <DialogContent className="min-h-32">{children}</DialogContent>
      {showButtons && (
        <DialogActions>
          <Box className="flex justify-between w-full my-2">
            <WolfButton onClick={onClose} variant="outlined" color="secondary">
              {customCancelText || localeCommon['cancel']}
            </WolfButton>
            {showConfirmButton && (
              <WolfButton onClick={onConfirm} variant="contained" color={customConfirmColor || 'primary'} autoFocus>
                {customConfirmText || localeCommon['confirm']}
              </WolfButton>
            )}
          </Box>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CustomModal;
